import type { CreateShareStateResponse, ShareState, ShareStateResponse } from '@repo/map-user-data/share-state';
import { SHARE_STATE_TOKEN_QUERY_PARAM, serializeShareState } from '@repo/map-user-data/share-state';
import { clearMapFilterUrlParams } from './share-url-filters';

const OVERRIDE_URL = process.env.NEXT_PUBLIC_MAP_USER_DATA_URL;
const SAME_ORIGIN_BASE = '/maps/api/share-states';
const SHARE_STATE_CREATE_CACHE_TTL_MS = 60_000;

const shareStateCreateRequests = new Map<string, { expiresAt: number; request: Promise<string> }>();
const shareStateRequests = new Map<string, Promise<ShareState>>();

function getShareStatesBase(): string {
    return OVERRIDE_URL ? `${OVERRIDE_URL.replace(/\/$/, '')}/share-state` : SAME_ORIGIN_BASE;
}

function getShareStateError(body: unknown): { code: string; message: string } | null {
    if (typeof body !== 'object' || body === null || !('error' in body)) {
        return null;
    }

    const error = (body as { error?: unknown }).error;
    if (typeof error !== 'object' || error === null) {
        return null;
    }

    const { code, message } = error as { code?: unknown; message?: unknown };
    return typeof code === 'string' && typeof message === 'string' ? { code, message } : null;
}

export class ShareStateRequestError extends Error {
    readonly body: unknown;
    readonly code?: string;
    readonly status: number;

    constructor(response: Response, body: unknown) {
        const error = getShareStateError(body);
        super(error?.message ?? `share state request failed with ${response.status}`);
        this.name = 'ShareStateRequestError';
        this.body = body;
        this.code = error?.code;
        this.status = response.status;
    }
}

async function parseResponse<T>(response: Response): Promise<T> {
    const text = await response.text();
    let body: unknown = null;
    try {
        body = text ? (JSON.parse(text) as unknown) : null;
    } catch {
        if (response.ok) {
            throw new Error('share state response was not valid JSON');
        }
        body = text;
    }

    if (!response.ok) {
        throw new ShareStateRequestError(response, body);
    }
    return body as T;
}

export async function createShareState(state: ShareState): Promise<string> {
    const payload = serializeShareState(state);
    const now = Date.now();
    const existingRequest = shareStateCreateRequests.get(payload);
    if (existingRequest && existingRequest.expiresAt > now) {
        return existingRequest.request;
    }

    for (const [key, entry] of shareStateCreateRequests) {
        if (entry.expiresAt <= now) {
            shareStateCreateRequests.delete(key);
        }
    }

    const request = (async () => {
        const response = await fetch(getShareStatesBase(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
        });
        const body = await parseResponse<CreateShareStateResponse>(response);
        return body.token;
    })();

    shareStateCreateRequests.set(payload, { expiresAt: now + SHARE_STATE_CREATE_CACHE_TTL_MS, request });
    void request.catch(() => {
        if (shareStateCreateRequests.get(payload)?.request === request) {
            shareStateCreateRequests.delete(payload);
        }
    });
    return request;
}

export async function getShareState(token: string): Promise<ShareState> {
    const existingRequest = shareStateRequests.get(token);
    if (existingRequest) {
        return existingRequest;
    }

    const request = fetch(`${getShareStatesBase()}/${encodeURIComponent(token)}`)
        .then(async response => {
            const body = await parseResponse<ShareStateResponse>(response);
            return body.state;
        })
        .catch(error => {
            shareStateRequests.delete(token);
            throw error;
        });
    shareStateRequests.set(token, request);
    return request;
}

export function createShareStateUrl(baseUrl: string, token: string): string {
    const url = new URL(baseUrl);
    clearMapFilterUrlParams(url);
    url.searchParams.set(SHARE_STATE_TOKEN_QUERY_PARAM, token);
    return url.toString();
}
